'use client';

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import useMediaQuery from '@/helpers/hooks/useMediaQuery';
import { desktop, mediumDesktop } from '@/helpers/utils/screensizes';
import ProjectDetails from './components/create-project';
import AddProducts from './components/addProducts';
import ViewProducts from './components/viewProducts';
import ProjectSummary from './components/project-summary';
import useAccount from '@/lib/hooks/useAccount';
import useProjectBuilder from '@/lib/hooks/useProjectBuilder';
import Typography from '@/components/atoms/project-builder/typography';
import Spinner from '@/components/organisms/spinner';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import ProjectLayout from './layout';
import { ctDataToFormData } from './components/data';
import { useWarnIfUnsavedChanges } from '@/helpers/hooks/useWarnIfUnsavedChanges';
import Dashboard from '@/components/pages/dashboard';

export interface ProjectFormData {
  projectLocation: {
    name: string;
    value: string;
  };
  projectName: string;
  projectType: {
    name: string;
    value: string;
  };
  projectStatus: {
    name: string;
    value: string;
  };
  username: string;
  selectedRoomDetails: [];
  lineItems?: [];
  shoppingListId?: string;
  projectFormData?: string;
  projectVersion?: string;
  customerEmail?: string;
  customerName?: string;
  customerPhone?: string;
  customerAddress?: string;
}

const ProjectBuilder = ({ data, selectDataForPublishedProducts }: any) => {
  const router = useRouter();
  const { loggedIn } = useAccount();
  const pathname = usePathname();
  const { getProjectById, updateProjectStatus } = useProjectBuilder();

  const searchParams = useSearchParams();
  const projectIdToEdit = searchParams.get('projectId') as string;

  const isEditPage = pathname.indexOf('/edit') !== -1;
  const isViewPage = pathname.indexOf('/view') !== -1;
  const getInitialActiveTab = () => {
    const tabParam = searchParams.get('activeTab');
    if (tabParam) {
      const tabNumber = parseInt(tabParam, 10);
      if (tabNumber >= 1 && tabNumber <= 4) {
        return tabNumber;
      }
    }
    return 1;
  };

  const [activeTab, setActiveTab] = useState(getInitialActiveTab());
  const [loading, setLoading] = useState(true);
  const getInitialTitle = () => (isEditPage || isViewPage ? 'Edit Project' : 'Create Project');
  const [projectTitle, setProjectTitle] = useState(getInitialTitle());
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
  const [projectFormData, setProjectFormData] = useState<ProjectFormData | null>(null);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const isFormDirtyRef = useRef(isFormDirty);

  const [openSaveConfirmation, setOpenSaveConfirmation] = useState<boolean>(false);
  const openSaveConfirmationRef = useRef(openSaveConfirmation);
  const [tabIndexValue, setTabIndexValue] = useState(0);
  const { showModal, handleConfirmLeave, handleCancelLeave } = useWarnIfUnsavedChanges(isFormDirty);

  useEffect(() => {
    setOpenSaveConfirmation(showModal);
  }, [showModal]);
  useEffect(() => {
      setLoading(false);
  }, [loggedIn]);

  const updateProjectData = (projectData: any, tabIndex: number) => {
    setProjectFormData(projectData);
    if ([3, 4].includes(tabIndex)) {
      setProjectTitle(projectData?.projectName || '');
    } else if (tabIndex === 2) {
      setProjectTitle(`Add products to ${projectData?.projectName || ''}`);
    } else {
      setProjectTitle(getInitialTitle());
    }
  };

  const updateURLWithTab = (tabIndex: number) => {
    const current = new URLSearchParams(Array.from(searchParams.entries()));
    current.set('activeTab', tabIndex.toString());
    const search = current.toString();
    const query = search ? `?${search}` : '';
    const newUrl = `${pathname}${query}`;
    
    window.history.replaceState(window.history.state, '', newUrl);
  };

  const fetchAndUpdateProjectData = async (shoppingListId: string, tabIndex: number) => {
    const projectData: any = await getProjectById({ shoppingListId });
    updateProjectData(ctDataToFormData(projectData?.data) ?? {}, tabIndex);
  };
  
  const handleTabClick = useCallback(
    async (tabIndex: number, productData?: any) => {
      setTabIndexValue(tabIndex);
      if (isFormDirtyRef.current && !openSaveConfirmationRef.current) {
        setOpenSaveConfirmation(true);
        return;
      }

      setActiveTab(tabIndex);
      updateURLWithTab(tabIndex);

      if (productData && !projectIdToEdit) {
        await fetchAndUpdateProjectData(productData?.id || productData?.shoppingListId, tabIndex);
      } else if (projectIdToEdit) {
        await fetchAndUpdateProjectData(projectIdToEdit, tabIndex);
      } else if (projectFormData?.shoppingListId) {
        await fetchAndUpdateProjectData(projectFormData.shoppingListId, tabIndex);
      }
    },
    [projectIdToEdit, searchParams, pathname, router, projectFormData?.shoppingListId],
  );

  useEffect(() => {
    isFormDirtyRef.current = isFormDirty;
    openSaveConfirmationRef.current = openSaveConfirmation;
  }, [isFormDirty, openSaveConfirmation]);

  const viewProductsContent = useMemo(() => (
    <ViewProducts
      projectForm={projectFormData}
      handleTabClick={handleTabClick}
      setIsFormDirty={setIsFormDirty}
      openSaveConfirmation={openSaveConfirmation}
      setOpenSaveConfirmation={setOpenSaveConfirmation}
      tabIndexValue={tabIndexValue}
      setTabIndexValue={setTabIndexValue}
      handleConfirmLeave={handleConfirmLeave}
      handleCancelLeave={handleCancelLeave}
      selectDataForPublishedProducts={selectDataForPublishedProducts}
    />
  ), [
    projectFormData,
    handleTabClick,
    setIsFormDirty,
    openSaveConfirmation,
    setOpenSaveConfirmation,
    tabIndexValue,
    setTabIndexValue,
    handleConfirmLeave,
    handleCancelLeave,
    selectDataForPublishedProducts,
  ]);

  const tabs = useMemo(
    () => [
      {
        id: 1,
        label: 'Project details',
        content: (
          <ProjectDetails
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabClick={handleTabClick}
            projectFormData={projectFormData}
            setIsFormDirty={setIsFormDirty}
            isFormDirty={isFormDirty}
            openSaveConfirmation={openSaveConfirmation}
            setOpenSaveConfirmation={setOpenSaveConfirmation}
            handleCancelLeave={handleCancelLeave}
            handleConfirmLeave={handleConfirmLeave}
            tabIndexValue={tabIndexValue}
          />
        ),
        disabled: false,
      },
      {
        id: 2, label: 'Add products', content: !isMediumDesktop ? <AddProducts projectForm={projectFormData} /> : null,
        disabled: !projectFormData
      },
      {
        id: 3,
        label: 'View products',
        content: viewProductsContent,
        disabled: !projectFormData
      },
      {
        id: 4,
        label: 'Preview',
        content: (
          <ProjectSummary projectFormData={projectFormData} handleTabClick={handleTabClick} data={data?.data_banner} selectDataForPublishedProducts={selectDataForPublishedProducts} />
        ),
        disabled: !projectFormData
      },
    ],
    [isMediumDesktop, activeTab, projectFormData, openSaveConfirmation, viewProductsContent],
  );

  useEffect(() => {
    if (projectIdToEdit) {
      const initialTab = getInitialActiveTab();
      const defaultTab = isViewPage ? 3 : 1;
      const tabToUse = searchParams.get('activeTab') ? initialTab : defaultTab;
      handleTabClick(tabToUse);
    }
  }, []);

  if (loading || ((isEditPage || isViewPage) && projectIdToEdit && !projectFormData)) {
    return <>
      <Spinner />
      <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
    </>;
  }

  const createProject = () => {
    return (
      <div className={`${!isMediumDesktop ? 'mobile' : 'desktop'}`}>
        <Typography className="fontSize32-sm px-2 2xl:px-0 xl:mt-0 mt-2 md:mt-5 md:!mb-[20px] whitespace-normal break-words text-black">
          {projectTitle}
        </Typography>
        {isMediumDesktop && (
          <>
            <div className="tab-row">
              {tabs.map((tab, index) => (
                <div key={tab.id} className="tab-item">
                  <button
                    className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => handleTabClick(tab.id, projectFormData)}
                    disabled={tab.disabled}
                  >
                    <span className={`fontSize18-sm circle ${activeTab === tab.id ? 'active-circle' : ''}`}>
                      {index + 1}
                    </span>
                    <p className="text-[18px] xl:text-[15px] 2xl:text-[18px]">{tab.label}</p>
                  </button>
                  {index < tabs.length - 1 && <div className="line horizontal-line"></div>}
                </div>
              ))}
            </div>
            {activeTab !== 2 && <div className="tab-content">{tabs.find((tab) => tab.id === activeTab)?.content}</div>}
          </>
        )}
        {!isMediumDesktop &&
          tabs.map((tab, index) => (
            <div key={tab.id} className="tab-section">
              <div className="tab-heading">
                <button
                  className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabClick(tab.id, projectFormData)}
                  disabled={tab.disabled}
                >
                  <span className={`circle ${activeTab === tab.id ? 'active-circle' : ''}`}>{index + 1}</span>
                  {tab.label}
                </button>
                <div className="inline-flex w-full">
                  {!(activeTab === 2 && activeTab === tab.id) && (
                    <div className={`${activeTab === tab.id ? 'vertical-line-active' : 'vertical-line'}`}></div>
                  )}
                  {activeTab === tab.id && <div className="tab-content indent-content w-full">{tab.content}</div>}
                </div>
              </div>
            </div>
          ))}
      </div>
    );
  };

  return !loggedIn ? (
    <>
    <Spinner />
    <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
    </>
  ) : (
    <>
     <Dashboard
        title={'My Projects'}
        href="/project-builder"
      >
      {isMediumDesktop ? (
        <ProjectLayout activeTab={activeTab}
          projectForm={projectFormData} >{createProject()}</ProjectLayout>
      ) : (
        <div className={`p-[8px] md:p-15`} id="project-builder">
          {createProject()}
        </div>
      )}
</Dashboard>
{(activeTab === 2 && isMediumDesktop) && <div className="bg-[#F3EDE9] px-4 pt-10 lg:px-10 dashboard !pb-6"><AddProducts projectForm={projectFormData} /></div>}
</>
  );
};

export default ProjectBuilder;
